<script lang="ts" setup>
import StorageService from '../../../runtime/services/StorageService';

const { $log } = useLogging();
const { $alert } = useAlerts();
const { $t } = useLocalization();

const emit = defineEmits<{
    (e: 'reload'): void;
}>();

const resource = ref<FileResource>(null);

const folderForm = ref<any>(Form.create({ folderName: '' }));
const folderModalRef = ref<any>(null);
const folderInputRef = ref<any>(null);

const fileForm = ref<any>(Form.create({ fileName: '' }));
const fileModalRef = ref<any>(null);
const fileInputRef = ref<any>(null);

const showModal = (item: FileResource): void =>
{
    resource.value = item;

    const { resourceType, name } = resource.value;

    if (resourceType === 'file')
    {
        fileForm.value.fileName = name;
        fileModalRef.value.show();
    }
    else
    {
        folderForm.value.folderName = name;
        folderModalRef.value.show();
    }
};

const renameFile = async (): Promise<void> =>
{
    try
    {
        const { id } = resource.value;

        fileForm.value.wait();

        await StorageService.renameFile(id, fileForm.value.fileName);

        $alert.success($t('[[[Nazwa pliku została zmieniona.]]]'));
        fileModalRef.value.hide();
        emit('reload');
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message),
            422: (ex: any) => fileForm.value.$errors.record(ex.data.errors)
        });
    }
    finally
    {
        fileForm.value.continue();
    }
};

const renameFolder = async (): Promise<void> =>
{
    try
    {
        const { id } = resource.value;

        folderForm.value.wait();

        await StorageService.renameFolder(id, folderForm.value.folderName);

        $alert.success($t('[[[Nazwa pliku została zmieniona.]]]'));
        folderModalRef.value.hide();
        emit('reload');
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message),
            422: (ex: any) => folderForm.value.$errors.record(ex.data.errors)
        });
    }
    finally
    {
        folderForm.value.continue();
    }
};

defineExpose({
    showModal
});
</script>

<template>
    <IdeoModal
        ref="fileModalRef"
        :title="$t('[[[Zmień nazwę pliku]]]')"
        hide-footer
        modal-class="filemanager-modal"
        backdrop-class="filemanager-backdrop"
        @shown="fileInputRef.focus()"
    >
        <form @submit.prevent="renameFile" @keydown="fileForm.$errors.clear($event.target.name)">
            <IdeoFormGroup
                :label="$t('[[[Nazwa pliku]]]')"
                :invalid-feedback="fileForm.$errors.first('fileName')"
                :state="fileForm.$errors.state('fileName')"
                label-cols="3"
                label-for="file-name"
            >
                <IdeoFormInput
                    id="file-name"
                    ref="fileInputRef"
                    v-model="fileForm.fileName"
                    type="text"
                    name="fileName"
                />
            </IdeoFormGroup>

            <div class="text-end">
                <ActionButton
                    variant="light"
                    icon="far fa-ban"
                    :text="$t('[[[Anuluj]]]')"
                    :disabled="!fileForm.active()"
                    @click.prevent="fileModalRef.hide()"
                />

                <ActionButton
                    type="submit"
                    variant="success"
                    icon="far fa-check"
                    :text="$t('[[[Zapisz]]]')"
                    :loading="!fileForm.active()"
                />
            </div>
        </form>
    </IdeoModal>

    <IdeoModal
        ref="folderModalRef"
        :title="$t('[[[Zmień nazwę katalogu]]]')"
        hide-footer
        modal-class="filemanager-modal"
        backdrop-class="filemanager-backdrop"
        @shown="folderInputRef.focus()"
    >
        <form @submit.prevent="renameFolder" @keydown="folderForm.$errors.clear($event.target.name)">
            <IdeoFormGroup
                :label="$t('[[[Nazwa katalogu]]]')"
                :invalid-feedback="folderForm.$errors.first('folderName')"
                :state="folderForm.$errors.state('folderName')"
                label-cols="3"
            >
                <IdeoFormInput
                    ref="folderInputRef"
                    v-model="folderForm.folderName"
                    type="text"
                    name="folderName"
                />
            </IdeoFormGroup>

            <div class="text-end">
                <ActionButton
                    variant="light"
                    icon="far fa-ban"
                    :text="$t('[[[Anuluj]]]')"
                    :disabled="!folderForm.active()"
                    @click.prevent="folderModalRef.hide()"
                />

                <ActionButton
                    type="submit"
                    variant="success"
                    icon="far fa-check"
                    :text="$t('[[[Zapisz]]]')"
                    :loading="!folderForm.active()"
                />
            </div>
        </form>
    </IdeoModal>
</template>
